import React, { useEffect, useMemo, useState } from "react";

/**
 * DeveloperPortfolioBootstrap.jsx
 * - Page portfolio React en **Bootstrap 5.3** (sans Tailwind)
 * - Sections : Hero • À propos • Compétences • Projets • Expérience • Contact
 * - Navbar sticky + scroll fluide + bascule de thème (clair/sombre) via data-bs-theme
 *
 * PRÉREQUIS (index.html ou _document):
 *  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet" />
 *  <link href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css" rel="stylesheet" />
 *  <script defer src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"></script>
 */

const DATA = {
  name: "Votre Nom",
  role: "Développeur Full‑Stack (React | Node)",
  location: "Lomé, Togo",
  email: "votremail@example.com",
  phone: "+228 90 00 00 00",
  socials: {
    github: "https://github.com/Benk-2001",
    linkedin: "https://www.linkedin.com/in/username/",
  },
  intro:
    "J'aide les entreprises à concevoir des applications web performantes, élégantes et scalables. Passionné par l'UX, la qualité du code et la livraison continue.",
  skills: [
    "React", "PHP", "Next.js", "Node.js","NestJS", "Firebase","CI/CD", "Flutter", "Bootstrap", "Dot net",
  ],
  projects: [
    {
      title: "Marketplace Auto",
      tagline: "Vente/location de véhicules (React + Firebase)",
      stack: ["React", "Firebase", "Cloud Functions"],
      link: "https://exemple-projet-1.com",
    },
    {
      title: "E‑commerce Grocery",
      tagline: "Panier, paiement mobile money (React + Laravel)",
      stack: ["React", "Laravel", "MySQL"],
      link: "https://exemple-projet-2.com",
    },
    {
      title: "Dashboard Analytics",
      tagline: "KPIs temps réel, rôles & permissions (Next.js + Supabase)",
      stack: ["Next.js", "Supabase", "Bootstrap"],
      link: "https://exemple-projet-3.com",
    },
  ],
  experience: [
    {
      company: "THE BRIDGE SARL",
      role: "Développeur Front‑end React",
      period: "2023 — 2025",
      bullets: [
        "Refonte UI, performance Lighthouse +35%.",
        "Design system et composants réutilisables.",
        "Intégration Firestore + Storage + Functions.",
      ],
    },
    {
      company: "DollarStore",
      role: "Full‑Stack Developer",
      period: "2022 — 2024",
      bullets: [
        "API Node/Express pour catalogue & commandes.",
        "CI/CD Vercel & Cloud Run, observabilité.",
        "Optimisations SQL et indexation.",
      ],
    },
  ],
};

export default function DeveloperPortfolio() {
  const [theme, setTheme] = useState("dark"); // "light" | "dark"
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    // Applique le thème Bootstrap 5.3
    document.documentElement.setAttribute("data-bs-theme", theme);
  }, [theme]);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-body min-vh-100">
      {/* NAVBAR */}
      <nav className="navbar navbar-expand-lg sticky-top border-bottom bg-body-tertiary">
        <div className="container">
          <a className="navbar-brand fw-semibold" href="#home" onClick={(e)=>handleNavClick(e, 'home')}>
            <i className="bi bi-code-slash me-2"/> {DATA.name.split(" ")[0] || "Portfolio"}
          </a>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navContent" aria-controls="navContent" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navContent">
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0 align-items-lg-center gap-lg-3">
              <li className="nav-item"><a className="nav-link" href="#projects" onClick={(e)=>handleNavClick(e,'projects')}>Projets</a></li>
              <li className="nav-item"><a className="nav-link" href="#experience" onClick={(e)=>handleNavClick(e,'experience')}>Expérience</a></li>
              <li className="nav-item"><a className="nav-link" href="#contact" onClick={(e)=>handleNavClick(e,'contact')}>Contact</a></li>
              <li className="nav-item">
                <a
                  href="#cv"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                  onClick={(e)=>{e.preventDefault(); alert('Remplace ce lien par ton CV (PDF) — ex: /cv_nom.pdf');}}
                >
                  <i className="bi bi-download me-2"/>CV
                </a>
              </li>
              <li className="nav-item">
                <button className="btn btn-outline-primary btn-sm" onClick={()=>setTheme(t=>t==='dark'?'light':'dark')} title="Basculer le thème">
                  {theme === 'dark' ? <i className="bi bi-sun"/> : <i className="bi bi-moon"/>}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="home" className="py-5">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-md-7">
              <span className="badge text-bg-secondary-subtle border border-secondary-subtle rounded-pill mb-3 d-inline-flex align-items-center">
                <i className="bi bi-rocket-takeoff me-2"/> Disponible pour mission/emploi
              </span>
              <h1 className="display-5 fw-bold">{DATA.name}</h1>
              <p className="fs-5 text-body-secondary mb-2">{DATA.role}</p>
              <p className="text-body-secondary">{DATA.intro}</p>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <a href={`mailto:${DATA.email}`} className="btn btn-primary d-inline-flex align-items-center">
                  <i className="bi bi-envelope me-2"/> Me contacter
                </a>
                <a href={DATA.socials.github} target="_blank" rel="noreferrer" className="btn btn-outline-secondary d-inline-flex align-items-center">
                  <i className="bi bi-github me-2"/> GitHub
                </a>
                <a href={DATA.socials.linkedin} target="_blank" rel="noreferrer" className="btn btn-outline-secondary d-inline-flex align-items-center">
                  <i className="bi bi-linkedin me-2"/> LinkedIn
                </a>
              </div>
              <div className="d-flex flex-wrap gap-4 mt-3 text-body-secondary small">
                <span className="d-inline-flex align-items-center"><i className="bi bi-geo-alt me-2"/>{DATA.location}</span>
                <span className="d-inline-flex align-items-center"><i className="bi bi-telephone me-2"/>{DATA.phone}</span>
              </div>
            </div>
            <div className="col-md-5">
              <div className="ratio ratio-1x1 rounded-4 border border-secondary-subtle bg-body-secondary position-relative overflow-hidden">
                <i className="bi bi-code-square position-absolute top-50 start-50 translate-middle display-3 opacity-50"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT + SKILLS */}
      <section id="about" className="pb-4">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-4">
              <h2 className="h4 fw-bold">À propos</h2>
              <p className="text-body-secondary">Développeur orienté produit, je transforme les besoins métiers en interfaces claires, performantes et testées.</p>
            </div>
            <div className="col-lg-8">
              <h3 className="small text-uppercase text-body-secondary">Compétences principales</h3>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {DATA.skills.map((s)=> (
                  <span key={s} className="badge rounded-pill text-bg-secondary-subtle border border-secondary-subtle" style={{color:"black"}}>{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-5 border-top">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h3 fw-bold">Projets sélectionnés</h2>
            <a href={DATA.socials.github} target="_blank" rel="noreferrer" className="link-underline link-underline-opacity-0 d-inline-flex align-items-center">
              Voir plus sur GitHub <i className="bi bi-box-arrow-up-right ms-2"/>
            </a>
          </div>
          <div className="row g-4 mt-1">
            {DATA.projects.map((p)=> (
              <div key={p.title} className="col-12 col-sm-6 col-lg-4">
                <article className="card h-100 border-0 shadow-sm">
                  <div className="ratio ratio-16x9 bg-body-secondary"/>
                  <div className="card-body">
                    <h3 className="h6 fw-semibold mb-1">{p.title}</h3>
                    <p className="small text-body-secondary mb-2">{p.tagline}</p>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {p.stack.map((t)=> (
                        <span key={t} className="badge text-bg-light border">{t}</span>
                      ))}
                    </div>
                    <a href={p.link} target="_blank" rel="noreferrer" className="stretched-link small fw-semibold">
                      Voir le projet <i className="bi bi-arrow-right"/>
                    </a>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPÉRIENCE (timeline) */}
      <section id="experience" className="py-5 border-top">
        <div className="container">
          <h2 className="h3 fw-bold mb-4">Expérience</h2>
          <div className="position-relative ps-4">
            <div className="position-absolute top-0 start-0 h-100 border-start border-secondary-subtle"></div>
            <ul className="list-unstyled d-grid gap-4">
              {DATA.experience.map((exp, idx)=> (
                <li key={idx} className="position-relative">
                  <span className="position-absolute start-0 translate-middle-y bg-primary rounded-circle" style={{width:10,height:10, top: '1.25rem'}}></span>
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <h3 className="h6 fw-semibold mb-0 d-flex align-items-center gap-2"><i className="bi bi-briefcase"/> {exp.role} — {exp.company}</h3>
                        <span className="badge text-bg-light">{exp.period}</span>
                      </div>
                      <ul className="small text-body-secondary mt-3 mb-0 ps-3">
                        {exp.bullets.map((b,i)=> <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-5 border-top">
        <div className="container">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 p-sm-5">
              <h2 className="h3 fw-bold">Travaillons ensemble</h2>
              <p className="text-body-secondary">Vous avez un projet, une idée ou un besoin d'accompagnement technique ? J'apprécie les missions claires avec un vrai impact.</p>
              <div className="row g-3 mt-2">
                <div className="col-12 col-md-4">
                  <a href={`mailto:${DATA.email}`} className="btn btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center">
                    <i className="bi bi-envelope me-2"/> {DATA.email}
                  </a>
                </div>
                <div className="col-12 col-md-4">
                  <a href={`tel:${DATA.phone.replace(/\s/g,'')}`} className="btn btn-outline-secondary w-100 d-inline-flex align-items-center justify-content-center">
                    <i className="bi bi-telephone me-2"/> {DATA.phone}
                  </a>
                </div>
                <div className="col-12 col-md-4">
                  <a href={DATA.socials.linkedin} target="_blank" rel="noreferrer" className="btn btn-outline-dark w-100 d-inline-flex align-items-center justify-content-center">
                    <i className="bi bi-linkedin me-2"/> LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-4 border-top">
        <div className="container d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 small text-body-secondary">
          <div className="d-flex align-items-center gap-3">
            <a href={DATA.socials.github} target="_blank" rel="noreferrer" className="link-underline-opacity-0 d-inline-flex align-items-center"><i className="bi bi-github me-2"/> GitHub</a>
            <a href={DATA.socials.linkedin} target="_blank" rel="noreferrer" className="link-underline-opacity-0 d-inline-flex align-items-center"><i className="bi bi-linkedin me-2"/> LinkedIn</a>
          </div>
          <p className="mb-0">© {year} {DATA.name}. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}

/**
 * Intégration rapide
 * 1) Ajoute Bootstrap + Bootstrap Icons (liens CDN ci-dessus) dans public/index.html.
 *    OU installe: npm i bootstrap bootstrap-icons
 *       puis importe dans src/main.jsx ou src/index.js:
 *         import "bootstrap/dist/css/bootstrap.min.css";
 *         import "bootstrap-icons/font/bootstrap-icons.css";
 *         import "bootstrap/dist/js/bootstrap.bundle.min.js";
 *
 * 2) Place ce fichier (DeveloperPortfolioBootstrap.jsx) et importe-le dans App.jsx:
 *     import DeveloperPortfolio from "./DeveloperPortfolioBootstrap";
 *     export default function App(){ return <DeveloperPortfolio/> }
 *
 * 3) Remplace le contenu de l'objet DATA (infos perso, projets, liens, etc.).
 */
